import {
  Col,
  Modal,
  Row,
  Spin,
  Tooltip,
  Checkbox,
  message,
  Typography,
} from 'antd';
import { getTipoDePadronPorContenType } from 'api/catalogos';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import { getAdeudoPadronAsync } from 'hooks/useAdeudoPadron';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { recalculateCargos, sortCargos } from 'utils/cargos';
import currencyFormater from 'utils/toCurrency';
import { descuentosRender } from 'views/Recaudacion/Caja/PanelPadron';

const isDev = process.env.REACT_APP_ENV === 'PRODUCTION';

const ModalCartera = ({
  visible,
  onCancel,
  objectId,
  contenttype,
  sistema,
  setEjecucion,
  disabled = false,
}) => {
  // Datos base provenientes del API (sin recálculo)
  const [baseData, setBaseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Datos recalculados y ordenados para mostrar en la tabla
  const displayData = useMemo(
    () => sortCargos(recalculateCargos(baseData, selectedRowKeys)),
    [baseData, selectedRowKeys],
  );

  const totalAdeudo = useMemo(
    () => displayData
      .filter((e) => selectedRowKeys.includes(e.id))
      .reduce((acc, curr) => acc + curr.adeudo_total, 0),
    [displayData, selectedRowKeys],
  );

  // Funciones de selección basadas en PanelPadron
  const _onRowChildren = useCallback((record, keys) => {
    let cloneKeys = [...keys];
    const cargoChild = baseData.find(
      (e) => e.cargo_padre === record.id && !e.es_accesorio,
    );
    const cargosAccesorios = baseData?.filter(
      (e) => e.cargo_padre === record.id && e.es_accesorio,
    );
    cloneKeys = cloneKeys.filter((k) => !cargosAccesorios?.map((e) => e.id).includes(k));
    cloneKeys = cloneKeys.filter((a) => a !== record.id);
    cloneKeys = [...new Set(cloneKeys)];
    if (cargoChild) {
      return _onRowChildren(cargoChild, cloneKeys);
    }
    return cloneKeys;
  }, [baseData]);

  const _onRowFather = useCallback((record, keys) => {
    let cloneKeys = [...keys];
    const cargoPadre = baseData?.find((e) => (e.id === record.cargo_padre));
    const cargosAccesorios = baseData?.filter(
      (e) => e.cargo_padre === record.id && e.es_accesorio,
    );
    cloneKeys.push(record.id);
    cloneKeys = cloneKeys.concat(cargosAccesorios?.map((e) => e.id) || []);
    cloneKeys = [...new Set(cloneKeys)];
    if (cargoPadre) {
      return _onRowFather(cargoPadre, cloneKeys);
    }
    return cloneKeys;
  }, [baseData]);

  const onRow = useCallback((record) => {
    const checked = !selectedRowKeys.some((k) => k === record.id);
    const keys = checked
      ? _onRowFather(record, selectedRowKeys) : _onRowChildren(record, selectedRowKeys);
    setSelectedRowKeys(keys);
  }, [_onRowChildren, _onRowFather, selectedRowKeys]);

  const isDisabled = useCallback((record) => (record.es_accesorio && record.es_requerible)
      || (record?.convenios && [1, 4].includes(record?.convenios?.estados_globales))
      || !!record.tramite || record.disabled || disabled, [disabled]);

  // Columnas para la tabla de cargos
  const columns = useMemo(() => [
    isDev && {
      titleText: (
        <span>
          ID/TC (DEV)
        </span>
      ),
      dataIndex: 'id',
      key: 'id',
      width: 200,
    },
    isDev && {
      titleText: (
        <span>
          Tipo de Cargo
          <b>(DEV)</b>
        </span>
      ),
      dataIndex: 'tipo_de_cargo',
      key: 'tipo_de_cargo',
      width: 200,
      render: (val) => val.id || '',
    },
    {
      titleText: 'Periodo Fiscal',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 150,
      render: (_, record) => record.tipo_de_cargo?.periodo_fiscal?.periodo,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 350,
    },
    {
      titleText: 'Adeudo Total',
      dataIndex: 'adeudo_total',
      key: 'adeudo_total',
      render: (val) => currencyFormater(val),
      align: 'right',
      width: 150,
    },
    {
      titleText: 'Importe Base',
      dataIndex: 'importe',
      key: 'importe',
      width: 150,
      align: 'right',
      render: (val) => currencyFormater(val),
    },
    {
      titleText: 'Actualización',
      dataIndex: 'actualizaciones_base',
      key: 'actualizaciones_base',
      width: 125,
      align: 'right',
      render: (txt, record) => {
        const _actualizaciones = record.actualizaciones
          .filter((r) => r.estados_globales !== 5 && !r.saldado);
        const actualizaciones = _actualizaciones.length
          ? _actualizaciones.filter((i) => !i.es_multa).map((r) => (
            <li key={r.id}>
              {`${r.descripcion}: ${currencyFormater(r.importe_total)}`}
            </li>
          )) : null;

        return (
          <Tooltip title={actualizaciones?.length ? (<ul>{actualizaciones}</ul>) : null}>
            <span>{currencyFormater(txt)}</span>
          </Tooltip>
        );
      },
    },
    {
      titleText: 'Recargos',
      dataIndex: 'recargos_base',
      key: 'recargos_base',
      width: 150,
      align: 'right',
      render: (txt, record) => {
        const _recargos = record.recargos.filter((r) => r.estados_globales !== 5 && !r.saldado);
        const recargos = _recargos.length
          ? _recargos.filter((i) => !i.es_multa).map((r) => (
            <li key={r.id}>
              {`${r.descripcion}: ${currencyFormater(r.importe_total)}`}
            </li>
          )) : null;

        return (
          <Tooltip title={recargos?.length ? (<ul>{recargos}</ul>) : null}>
            <span>{currencyFormater(txt)}</span>
          </Tooltip>
        );
      },
    },
    {
      titleText: 'Multa',
      dataIndex: 'multas_base',
      key: 'multas_base',
      width: 125,
      align: 'right',
      // render: multasRender,
      render: (txt, record) => {
        const _recargos = record.recargos.filter((r) => r.estados_globales !== 5 && !r.saldado);
        const recargosMultas = _recargos.filter((i) => i.es_multa);
        const _gastos = record.gastos.filter((g) => g.estados_globales !== 5 && !g.saldado);
        const gastosMultas = _gastos.filter((i) => i.es_multa);
        const recargos = recargosMultas.map((r) => (
          <li key={r.id}>
            {`${r.descripcion}: ${currencyFormater(r.importe_total)}`}
          </li>
        ));
        const gastos = gastosMultas.map((g) => (
          <li key={g.id}>
            {`${g.descripcion}: ${currencyFormater(g.importe)}`}
          </li>
        ));
        const allMultas = [...recargos, ...gastos];
        return (
          <Tooltip title={allMultas?.length ? (<ul>{allMultas}</ul>) : null}>
            <span>{currencyFormater(txt)}</span>
          </Tooltip>
        );
      },
    },
    {
      titleText: 'Gastos',
      dataIndex: 'gastos_totales',
      key: 'gastos_totales',
      width: 125,
      align: 'right',
      // render: (txt, record) => renderAccesorios(txt, record, 'gastos'),
      render: (txt, record) => {
        const _gastos = record.gastos.filter((g) => g.estados_globales !== 5 && !g.saldado);
        const gastos = _gastos.filter((i) => !i.es_multa).map((g) => (
          <li key={g.id}>
            {`${g.descripcion}: ${currencyFormater(g.importe)}`}
          </li>
        ));
        return (
          <Tooltip title={gastos?.length ? (<ul>{gastos}</ul>) : null}>
            <span>{currencyFormater(txt)}</span>
          </Tooltip>
        );
      },
    },
    {
      titleText: 'Desc.',
      dataIndex: '_descuentos_totales_junto_accesorios',
      key: '_descuentos_totales_junto_accesorios',
      width: 125,
      align: 'right',
      render: descuentosRender,
    },
  ].filter((e) => e), [isDev]);

  // Columnas para móvil
  const mobileCols = useMemo(() => [
    {
      key: 'descripcion',
      dataIndex: 'descripcion',
      titleText: 'Descripción',
      width: 200,
    },
    {
      key: 'importe',
      dataIndex: 'importe',
      titleText: 'Importe',
      width: 120,
      render: (value) => `$${value?.toFixed(2) || '0.00'}`,
    },
  ], []);

  const fetchCargos = useCallback(async () => {
    if (!objectId || !contenttype || !sistema) {
      return;
    }

    setLoading(true);
    try {
      // Parámetros para la consulta

      const tipoDePadron = getTipoDePadronPorContenType(contenttype);
      const [cargos, error] = await getAdeudoPadronAsync(
        tipoDePadron, objectId, setLoadingMessage, null, sistema,
      );
      if (error) {
        message.error(error);
        return;
      }
      const filtered = (cargos || []).filter((e) => e?.tipo_de_cargo?.sistemas?.id === sistema);
      setBaseData(filtered);
    } catch (err) {
      setBaseData([]);
    } finally {
      setLoading(false);
    }
  }, [contenttype, objectId, sistema]);

  useEffect(() => {
    if (visible && objectId && contenttype && sistema) {
      fetchCargos();
    }
  }, [visible, objectId, contenttype, sistema, fetchCargos]);

  const handleCancel = useCallback(() => {
    setBaseData([]);
    setSelectedRowKeys([]);
    onCancel();
  }, [onCancel]);

  const handleFinish = useCallback(() => {
    if (!selectedRowKeys.length) {
      message.warn('No se seleccionaron cargos para cargar en la cartera.');
      return;
    }
    const cargos = displayData.filter((e) => selectedRowKeys.includes(e.id))
      .map((e) => ({
        ...e,
        descripcion: `(${e.tipo_de_cargo?.periodo_fiscal?.periodo}) ${e.descripcion}`,
      }));
    setEjecucion((prev) => ({
      ...prev,
      cargos,
    }));
    handleCancel();
  }, [displayData, handleCancel, selectedRowKeys, setEjecucion]);

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      onCancel={handleCancel}
      centered
      width="90%"
      title={(
        <FormSubmitControls
          onCancel={handleCancel}
          onFinish={handleFinish}
          loading={loading}
          label="Cargar Cartera"
        />
      )}
      destroyOnClose
    >
      <Spin spinning={loading} tip={loadingMessage}>
        <Row>
          <Col span={12}>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              Total del adeudo:
              {' '}
              {currencyFormater(totalAdeudo)}
            </Typography.Title>
          </Col>
          <Col span={24}>
            <Table
              cols={columns}
              mobileCols={mobileCols}
              data={displayData}
              noControls
              allowSearch
              allowSort
              loading={loading}
              rowKey="id"
              emptyText="No se encontraron cargos para este reporte moroso"
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                hideSelectAll: disabled,
                renderCell: (checked, record) => (
                  <Checkbox
                    checked={checked}
                    disabled={isDisabled(record)}
                  />
                ),
              }}
              pageSize={displayData?.length}
              onRow={(record) => ({
                onClick: (e) => {
                  e.stopPropagation();
                  if (!isDisabled(record)) {
                    onRow(record);
                  }
                },
              })}
              rowClassName={({ adeudo_total, es_accesorio }) => {
                let className = '';
                if (Number.isNaN(adeudo_total)) {
                  className += 'warn-row ';
                }
                if (!es_accesorio) {
                  className += 'bold';
                }
                return className;
              }}
            />
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

ModalCartera.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  objectId: PropTypes.number,
  contenttype: PropTypes.string,
  sistema: PropTypes.string,
  setEjecucion: PropTypes.func,
  disabled: PropTypes.bool,
};

ModalCartera.defaultProps = {
  objectId: null,
  contenttype: null,
  sistema: null,
  setEjecucion: () => {},
  disabled: false,
};

export default ModalCartera;
